import { stringify } from 'query-string';
import { UserId } from 'api/models';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { ApiFilter } from 'utils/simple-search-filter/simple-search-filter';
import { client } from '../client';
import {
  ApiReport,
  ApiReportCategoryRecord,
  ApiReportPhaseRecord,
  ApiReportProjectRecord,
  ApiReportRecord,
  ApiReportTaskRecord,
  ApiReportUserRecord,
} from './api-types';
import {
  asCategoryReport,
  asCategoryWiseSummary,
  asPhaseReport,
  asProjectReport,
  asProjectWiseSummary,
  asSummary,
  asTaskReport,
  asUserReport,
  asUserWiseSummary,
} from './mapper';
import {
  CategoryParam,
  ProjectParam,
  TimesheetCategoryReport,
  TimesheetCategoryWiseReportSummary,
  TimesheetPhaseReport,
  TimesheetProjectReport,
  TimesheetProjectWiseReportSummary,
  TimesheetReport,
  TimesheetReportExport,
  TimesheetReportSummary,
  TimesheetReportType,
  TimesheetTaskReport,
  TimesheetUserReport,
  TimesheetUserWiseReportSummary,
  UserParam,
} from './types';

const BASE_URL = '/timesheets/reports-light';

const getSummary = (
  startDate: string,
  endDate: string,
  filters: FieldFilter[],
  match: QueryCombinerType,
  includeAll = true,
): Promise<TimesheetReportSummary> => {
  return client
    .get<ApiReport<ApiReportUserRecord>>(
      `${BASE_URL}?${ApiFilter.toQueryStringFormat(filters)}&${stringify({
        groupBy: 'user',
        startDate,
        endDate,
        match: filters.length ? match : null,
        includeAll,
      })}`,
    )
    .then(asSummary);
};

const getProjectWiseSummary = (
  projectId: number | null,
  startDate?: string,
  endDate?: string,
): Promise<TimesheetProjectWiseReportSummary> =>
  client
    .get<ApiReport<ApiReportRecord>>(
      `${BASE_URL}?groupBy=user&projectId=${projectId}${
        startDate ? `&startDate=${startDate}` : ''
      }${endDate ? `&endDate=${endDate}` : ''}`,
    )
    .then(asProjectWiseSummary);

const getUserWiseSummary = (
  userId: number,
  startDate: string,
  endDate: string,
): Promise<TimesheetUserWiseReportSummary> =>
  client
    .get<ApiReport<ApiReportRecord>>(
      `${BASE_URL}?groupBy=project&userId=${userId}&startDate=${startDate}&endDate=${endDate}`,
    )
    .then(asUserWiseSummary);

const getCategoryWiseSummary = (
  categoryId: number | null,
  startDate: string,
  endDate: string,
): Promise<TimesheetCategoryWiseReportSummary> =>
  client
    .get<ApiReport<ApiReportRecord>>(
      `${BASE_URL}?groupBy=user&categoryId=${categoryId}&startDate=${startDate}&endDate=${endDate}`,
    )
    .then(asCategoryWiseSummary);

const getProjectReports = (
  startDate: string,
  endDate: string,
  filters: FieldFilter[],
  match: QueryCombinerType,
  includeAll = true,
): Promise<TimesheetProjectReport[]> =>
  client
    .get<ApiReport<ApiReportProjectRecord>>(
      `${BASE_URL}?${ApiFilter.toQueryStringFormat(filters)}&${stringify({
        groupBy: 'project',
        startDate,
        endDate,
        match: filters.length ? match : null,
        includeAll,
      })}`,
    )
    .then(asProjectReport);

const getCategoryReports = (
  startDate: string,
  endDate: string,
  filters: FieldFilter[],
  match: QueryCombinerType,
  includeAll = true,
): Promise<TimesheetCategoryReport[]> =>
  client
    .get<ApiReport<ApiReportCategoryRecord>>(
      `${BASE_URL}?${ApiFilter.toQueryStringFormat(filters)}&${stringify({
        groupBy: 'category',
        startDate,
        endDate,
        match: filters.length ? match : null,
        includeAll,
      })}`,
    )
    .then(asCategoryReport);

const getUserReports = (
  startDate: string,
  endDate: string,
  filters: FieldFilter[],
  match: QueryCombinerType,
  includeAll = true,
): Promise<TimesheetUserReport[]> =>
  client
    .get<ApiReport<ApiReportUserRecord>>(
      `${BASE_URL}?${ApiFilter.toQueryStringFormat(filters)}&${stringify({
        groupBy: 'user',
        startDate,
        endDate,
        match: filters.length ? match : null,
        includeAll,
      })}`,
    )
    .then(asUserReport);

const getReports = (
  startDate: string,
  endDate: string,
  filter: FieldFilter[],
  match: QueryCombinerType,
  groupBy: TimesheetReportType,
): Promise<TimesheetReport[]> => {
  if (groupBy === TimesheetReportType.Project) {
    return getProjectReports(startDate, endDate, filter, match);
  }
  if (groupBy === TimesheetReportType.User) {
    return getUserReports(startDate, endDate, filter, match);
  }

  return getCategoryReports(startDate, endDate, filter, match);
};

const getProjectWiseCategoryReports = (
  startDate: string,
  endDate: string,
  projectId: number | null,
): Promise<TimesheetCategoryReport[]> =>
  client
    .get<ApiReport<ApiReportCategoryRecord>>(
      `${BASE_URL}?groupBy=category&startDate=${startDate}&endDate=${endDate}&projectId=${projectId}`,
    )
    .then(asCategoryReport);

const getProjectWisePhaseReports = (
  startDate: string,
  endDate: string,
  projectId: number | null,
): Promise<TimesheetPhaseReport[]> =>
  client
    .get<ApiReport<ApiReportPhaseRecord>>(
      `${BASE_URL}?groupBy=phase&startDate=${startDate}&endDate=${endDate}&projectId=${projectId}`,
    )
    .then(asPhaseReport);

const getProjectWiseUserReports = (
  startDate: string,
  endDate: string,
  projectId: number | null,
): Promise<TimesheetUserReport[]> =>
  client
    .get<ApiReport<ApiReportUserRecord>>(
      `${BASE_URL}?groupBy=user&startDate=${startDate}&endDate=${endDate}&projectId=${projectId}`,
    )
    .then(asUserReport);

const getProjectWiseTaskReports = (
  startDate: string,
  endDate: string,
  projectId: number | null,
): Promise<TimesheetTaskReport[]> =>
  client
    .get<ApiReport<ApiReportTaskRecord>>(
      `${BASE_URL}?groupBy=task&startDate=${startDate}&endDate=${endDate}&projectId=${projectId}`,
    )
    .then(asTaskReport);

const getProjectWiseReports = (
  startDate: string,
  endDate: string,
  groupBy: TimesheetReportType,
  projectId: number | null,
): Promise<TimesheetReport[]> => {
  if (groupBy === TimesheetReportType.User) {
    return getProjectWiseUserReports(startDate, endDate, projectId);
  }

  if (groupBy === TimesheetReportType.Task) {
    return getProjectWiseTaskReports(startDate, endDate, projectId);
  }

  if (groupBy === TimesheetReportType.Phase) {
    return getProjectWisePhaseReports(startDate, endDate, projectId);
  }

  return getProjectWiseCategoryReports(startDate, endDate, projectId);
};

const getUserWiseProjectReports = (
  startDate: string,
  endDate: string,
  userId: number,
): Promise<TimesheetProjectReport[]> =>
  client
    .get<ApiReport<ApiReportProjectRecord>>(
      `${BASE_URL}?groupBy=project&startDate=${startDate}&endDate=${endDate}&userId=${userId}`,
    )
    .then(asProjectReport);

const getUserWiseCategoryReports = (
  startDate: string,
  endDate: string,
  userId: number,
): Promise<TimesheetCategoryReport[]> =>
  client
    .get<ApiReport<ApiReportCategoryRecord>>(
      `${BASE_URL}?groupBy=category&startDate=${startDate}&endDate=${endDate}&userId=${userId}`,
    )
    .then(asCategoryReport);

const getUserWiseReports = (
  startDate: string,
  endDate: string,
  groupBy: TimesheetReportType,
  userId: number,
): Promise<TimesheetReport[]> => {
  if (groupBy === TimesheetReportType.Project) {
    return getUserWiseProjectReports(startDate, endDate, userId);
  }

  return getUserWiseCategoryReports(startDate, endDate, userId);
};

const getCategoryWiseProjectReports = (
  startDate: string,
  endDate: string,
  categoryId: number | null,
): Promise<TimesheetProjectReport[]> =>
  client
    .get<ApiReport<ApiReportProjectRecord>>(
      `${BASE_URL}?groupBy=project&startDate=${startDate}&endDate=${endDate}&categoryId=${categoryId}`,
    )
    .then(asProjectReport);

const getCategoryWiseUserReports = (
  startDate: string,
  endDate: string,
  categoryId: number | null,
): Promise<TimesheetUserReport[]> =>
  client
    .get<ApiReport<ApiReportUserRecord>>(
      `${BASE_URL}?groupBy=user&startDate=${startDate}&endDate=${endDate}&categoryId=${categoryId}`,
    )
    .then(asUserReport);

const getCategoryWiseReports = (
  startDate: string,
  endDate: string,
  groupBy: TimesheetReportType,
  categoryId: number | null,
): Promise<TimesheetReport[]> => {
  return groupBy === TimesheetReportType.Project
    ? getCategoryWiseProjectReports(startDate, endDate, categoryId)
    : getCategoryWiseUserReports(startDate, endDate, categoryId);
};

const getResourceSpecificParam = (
  params: ProjectParam | UserId | CategoryParam,
) => {
  switch (params[0]) {
    case 'project_id':
      switch (params[1]) {
        case 'non-project':
          return { projectId: 'null' };
        default:
          return { projectId: params[1] };
      }
    case 'user_id':
      return { userId: params[1] };
    case 'category_id':
      switch (params[1]) {
        case 'no-category':
          return { categoryId: 'null' };
        default:
          return { categoryId: params[1] };
      }
    default:
      return null;
  }
};
const exportAsCSV = (
  startDate: string,
  endDate: string,
  filters: FieldFilter[],
  match: QueryCombinerType,
  params?: ProjectParam | UserParam | CategoryParam,
): Promise<string> => {
  return client
    .get<TimesheetReportExport>(
      `time-entries/export?${ApiFilter.toQueryStringFormat(
        filters,
      )}&${stringify({
        startDate,
        endDate,
        match: filters.length ? match : null,
        ...(params != null ? getResourceSpecificParam(params) : null),
      })}`,
    )
    .then((response) => response.downloadUrl);
};

export const TimesheetReportsApi = {
  getSummary,
  getProjectWiseSummary,
  getUserWiseSummary,
  getCategoryWiseSummary,
  getReports,
  getProjectWiseReports,
  getUserWiseReports,
  getCategoryWiseReports,
  exportAsCSV,
};
